import { useState, useEffect } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Accordion, 
  AccordionSummary, 
  AccordionDetails,
  CircularProgress,
  Stack,
  IconButton,
  Avatar,
  Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RoomIcon from '@mui/icons-material/Room';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import SummarizeIcon from '@mui/icons-material/Summarize';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useChat } from '../hooks/useChat';
import { useHomeAttachmentsContext } from '../contexts/HomeAttachmentsContext';
import { useHomeAreasContext } from '../contexts/HomeAreasContext';
import ImageSlider from './ImageSlider';
import { useParams } from 'react-router-dom';
import { analytics } from '../services/analytics';
import { useAnalytics } from '../hooks/useAnalytics';
import { useAuth } from '../contexts/AuthContext';

// Update the props interface
interface GeneratedRoomsProps {
  onImageSelect?: (imageId: string) => void;
  onClose?: () => void;
  hideHeader?: boolean;
}

function GeneratedRooms({ onClose, hideHeader = false }: GeneratedRoomsProps) {
  const { areas: chatAreas, isLoadingMessages } = useChat();
  const { areas: dbAreas, isLoading: isLoadingAreas } = useHomeAreasContext();
  const { getAttachmentById, getAttachmentUrl } = useHomeAttachmentsContext();
  const { homeId } = useParams<{ homeId: string }>();
  const { user } = useAuth();
  const [expanded, setExpanded] = useState<string | false>(false);
  
  // Log areas_list_open analytics event when Generated Rooms view is opened
  useAnalytics(
    () => {
      if (user?.uuid && homeId) {
        const totalAreas = (dbAreas?.length || 0) + (chatAreas?.length || 0);
        analytics.logAreasListOpen(user.uuid, homeId, totalAreas);
      }
    },
    [homeId, user?.uuid, dbAreas?.length, chatAreas?.length]
  );

  // Reset expanded state when areas change
  useEffect(() => {
    setExpanded(false);
  }, [chatAreas, dbAreas]);

  const handleChange = (panel: string, areaName: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
    
    // Log area_detail_open analytics event when expanding an area
    if (isExpanded && user?.uuid && homeId) {
      analytics.logAreaDetailOpen(user.uuid, areaName, homeId);
    }
  };

  // Helper function to get image URL for an area's primary image
  const getAreaPrimaryImageUrl = (area: any): string | undefined => {
    if (!area.primary_image) return undefined;
    
    const attachment = getAttachmentById(area.primary_image);
    if (!attachment) return undefined;
    
    return getAttachmentUrl(attachment);
  };

  // Helper function to find area details from database areas
  const getAreaDetails = (areaName: string) => {
    return dbAreas.find(area => area.area_name === areaName);
  };

  const isLoading = isLoadingMessages || isLoadingAreas;

  return (
    <Paper 
      sx={{ 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRadius: hideHeader ? 0 : 2,
      }}
      elevation={hideHeader ? 0 : 2}
    >
      {!hideHeader && (
        <Box sx={{ 
          p: 2, 
          borderBottom: 1, 
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Box>
            <Typography variant="h6" fontWeight={500}>Generated Rooms</Typography>
            <Typography variant="body2" color="text.secondary">
              Areas identified by CribCritic AI
            </Typography>
          </Box>
          {onClose && (
            <IconButton 
              onClick={onClose}
              color="primary"
              aria-label="back to home details"
            >
              <ArrowBackIcon />
            </IconButton>
          )}
        </Box>
      )}
      
      <Box sx={{ flex: 1, overflow: 'auto', p: hideHeader ? 0 : 1 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress size={30} />
          </Box>
        ) : chatAreas.length === 0 ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            height: '100%',
            p: 3,
            textAlign: 'center'
          }}>
            <RoomIcon sx={{ fontSize: 40, color: 'text.secondary', opacity: 0.5, mb: 2 }} />
            <Typography color="text.secondary">
              No rooms identified yet. Chat with the AI to analyze this home.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1}>
            {chatAreas.map((area) => {
              const primaryImageUrl = getAreaPrimaryImageUrl(area);
              const areaDetails = getAreaDetails(area.area_name);
              
              return (
                <Accordion 
                  key={area.area_name}
                  expanded={expanded === area.area_name}
                  onChange={handleChange(area.area_name, area.area_name)}
                  sx={{
                    backgroundImage: 'none',
                    '&:before': { display: 'none' }
                  }}
                >
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ 
                      alignItems: 'center',
                      '& .MuiAccordionSummary-content': {
                        alignItems: 'center'
                      }
                    }}
                  >
                    {primaryImageUrl && (
                      <Avatar 
                        src={primaryImageUrl}
                        alt={area.area_name}
                        sx={{ 
                          width: 32, 
                          height: 32, 
                          mr: 1.5,
                          border: 1,
                          borderColor: 'divider'
                        }}
                      />
                    )}
                    <Typography fontWeight={500}>{area.area_name}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Area Images
                        </Typography>
                        <Box sx={{ height: 150 }}>
                          <ImageSlider 
                            areaId={area.area_name} 
                            height={120}
                          />
                        </Box>
                      </Box>
                      
                      <Divider />
                      
                      {/* Pros Section */}
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <ThumbUpIcon fontSize="small" sx={{ color: 'success.main', mr: 1 }} />
                          <Typography variant="subtitle2" color="text.secondary">
                            Pros
                          </Typography>
                        </Box>
                        <Typography variant="body2">
                          {areaDetails?.area_pro || "No pros identified yet."}
                        </Typography>
                      </Box>
                      
                      {/* Cons Section */}
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <ThumbDownIcon fontSize="small" sx={{ color: 'error.main', mr: 1 }} />
                          <Typography variant="subtitle2" color="text.secondary">
                            Cons
                          </Typography>
                        </Box>
                        <Typography variant="body2">
                          {areaDetails?.area_con || "No cons identified yet."}
                        </Typography>
                      </Box>
                      
                      {/* Overview Section */}
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <SummarizeIcon fontSize="small" sx={{ color: 'info.main', mr: 1 }} />
                          <Typography variant="subtitle2" color="text.secondary">
                            Overview
                          </Typography>
                        </Box>
                        <Typography variant="body2">
                          {areaDetails?.area_overall || "No overview available yet."}
                        </Typography>
                      </Box>
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Stack>
        )}
      </Box>
    </Paper>
  );
}

export default GeneratedRooms;